/**
 * Root application component.
 * Manages tab-based navigation between pages.
 *
 * Hash-based routing: the manifest opens the task pane at a specific page
 * via taskpane.html#<route>. On mount, the component reads location.hash
 * and sets the active page.
 *
 * Supported hashes (from manifest v0.3.1):
 *   #assistant, #formulas, #explain, #cleaning, #analysis,
 *   #chart, #summary, #translate, #functions, #help, #settings
 *
 * Context detection: when no explicit hash is set (or hash is #assistant),
 * the app optionally analyzes the Excel selection and suggests a relevant page.
 *
 * Dynamic ribbon: the useExcelContext hook listens for selection changes and
 * updates ribbon button enabled/disabled state via RibbonApi 1.1.
 */

import React, { useState, useEffect } from 'react';
import { TabList, Tab, Button } from '@fluentui/react-components';
import {
  SparkleRegular,
  CalculatorRegular,
  SparkleRegular as SparklesRegular,
  DataAreaRegular,
  DataLineRegular,
  DocumentRegular,
  TranslateRegular,
  CodeRegular,
  MoreHorizontalRegular,
  QuestionCircleRegular,
  SettingsRegular,
  BotRegular,
  BrainRegular,
} from '@fluentui/react-icons';
import { ChatPage } from './pages/ChatPage';
import { FormulaPage } from './pages/FormulaPage';
import { CleaningPage } from './pages/CleaningPage';
import { AnalysisPage } from './pages/AnalysisPage';
import { ChartPage } from './pages/ChartPage';
import { SummaryPage } from './pages/SummaryPage';
import { TranslatePage } from './pages/TranslatePage';
import { FunctionsPage } from './pages/FunctionsPage';
import { NaturalLanguagePage } from './pages/NaturalLanguagePage';
import { DashboardPage } from './pages/DashboardPage';
import { ReportBuilderPage } from './pages/ReportBuilderPage';
import { SpreadsheetBuilderPage } from './pages/SpreadsheetBuilderPage';
import { AIAgentPage } from './pages/AIAgentPage';
import { AILabsPage } from './pages/AILabsPage';
import { WorkbookBrainMain } from './components/WorkbookBrain/WorkbookBrainMain';
import { HelpPage } from './pages/HelpPage';
import { SettingsPage } from './pages/SettingsPage';
import { StartPage } from './pages/StartPage';
import { Header } from './components/common/Header';
import { SmartContextBar } from './components/common/SmartContextBar';
import { useExcelContext } from './hooks/useExcelContext';

/** All navigable page IDs. Exported for use by office/detect.ts. */
export type PageId =
  | 'start'
  | 'assistant'
  | 'formulas'
  | 'explain'
  | 'cleaning'
  | 'analysis'
  | 'chart'
  | 'summary'
  | 'translate'
  | 'functions'
  | 'natural-language'
  | 'dashboard'
  | 'report-builder'
  | 'spreadsheet-builder'
  | 'ai-agent'
  | 'ai-labs'
  | 'workbook-brain'
  | 'help'
  | 'settings';

const VALID_PAGES: ReadonlySet<string> = new Set<PageId>([
  'start', 'assistant', 'formulas', 'explain', 'cleaning', 'analysis',
  'chart', 'summary', 'translate', 'functions', 'natural-language', 'dashboard', 'report-builder', 'spreadsheet-builder', 'ai-agent', 'ai-labs', 'workbook-brain', 'help', 'settings',
]);

function getHashPage(): PageId | null {
  const raw = window.location.hash.replace('#', '').toLowerCase();
  if (VALID_PAGES.has(raw)) return raw as PageId;
  return null;
}

export const App: React.FC = () => {
  const [activePage, setActivePage] = useState<PageId>(getHashPage() ?? 'start');
  const [showMoreTabs, setShowMoreTabs] = useState(false);
  const excelContext = useExcelContext();
  const isCompactTabs = true;

  const primaryTabs: PageId[] = [
    'assistant',
    'formulas',
    'cleaning',
    'analysis',
  ];

  const secondaryTabs: Array<{ value: PageId; label: string; icon: () => JSX.Element }> = [
    { value: 'chart', label: 'Graphique', icon: () => <DataLineRegular /> },
    { value: 'summary', label: 'Resume', icon: () => <DocumentRegular /> },
    { value: 'translate', label: 'Traduire', icon: () => <TranslateRegular /> },
    { value: 'functions', label: 'Fonctions (M365)', icon: () => <CodeRegular /> },
    { value: 'natural-language', label: 'Opérations', icon: () => <MoreHorizontalRegular /> },
    { value: 'dashboard', label: 'Dashboard', icon: () => <DataLineRegular /> },
    { value: 'report-builder', label: 'Rapport', icon: () => <DocumentRegular /> },
    { value: 'spreadsheet-builder', label: 'Feuille', icon: () => <SparkleRegular /> },
    { value: 'ai-agent', label: 'Agent', icon: () => <BotRegular /> },
    { value: 'ai-labs', label: 'IA Avancee', icon: () => <BotRegular /> },
    { value: 'workbook-brain', label: 'Audit Classeur', icon: () => <BrainRegular /> },
    { value: 'help', label: 'Aide', icon: () => <QuestionCircleRegular /> },
    { value: 'settings', label: 'Configurer AI', icon: () => <SettingsRegular /> },
  ];

  // On mount: if no explicit hash -> start page
  useEffect(() => {
    const explicit = getHashPage();
    if (explicit) {
      setActivePage(explicit);
      return;
    }
    setActivePage('start');
  }, []);

  // Listen for hash changes (ribbon button clicked while task pane is open)
  useEffect(() => {
    const onHashChange = () => {
      const page = getHashPage();
      if (page) {
        setActivePage(page);
      }
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const handleTabSelect = (_event: unknown, data: { value: unknown }) => {
    const raw = data.value as string;
    if (raw === 'more') {
      setShowMoreTabs((v) => !v);
      return;
    }
    const page = raw as PageId;
    setActivePage(page);
    setShowMoreTabs(false);
    window.location.hash = page;
  };

  const handleContextNavigate = (page: PageId) => {
    setActivePage(page);
    setShowMoreTabs(false);
    window.location.hash = page;
  };

  const renderPage = () => {
    switch (activePage) {
      case 'start': return <StartPage onNavigate={handleContextNavigate} />;
      case 'assistant': return <ChatPage />;
      case 'formulas': return <FormulaPage initialTab="generate" />;
      case 'explain': return <FormulaPage initialTab="explain" />;
      case 'cleaning': return <CleaningPage />;
      case 'analysis': return <AnalysisPage />;
      case 'chart': return <ChartPage />;
      case 'summary': return <SummaryPage />;
      case 'translate': return <TranslatePage />;
      case 'functions': return <FunctionsPage />;
      case 'natural-language': return <NaturalLanguagePage />;
      case 'dashboard': return <DashboardPage />;
      case 'report-builder': return <ReportBuilderPage />;
      case 'spreadsheet-builder': return <SpreadsheetBuilderPage />;
      case 'ai-agent': return <AIAgentPage />;
      case 'ai-labs': return <AILabsPage />;
      case 'workbook-brain': return <WorkbookBrainMain />;
      case 'help': return <HelpPage />;
      case 'settings': return <SettingsPage />;
    }
  };

  // Determine which tab should appear selected.
  // #explain and #formulas both highlight the "formulas" tab.
  const normalizedActive = activePage === 'explain' ? 'formulas' : activePage;
  const selectedTabValue =
    isCompactTabs && !primaryTabs.includes(normalizedActive as PageId)
      ? 'more'
      : normalizedActive;

  return (
    <div className="app-container">
      <Header />
      <TabList
        selectedValue={selectedTabValue}
        onTabSelect={handleTabSelect}
        className="app-tabs"
        size="small"
      >
        <Tab value="assistant" icon={<SparkleRegular />}>Assistant</Tab>
        <Tab value="formulas" icon={<CalculatorRegular />}>Formules</Tab>
        <Tab value="cleaning" icon={<SparklesRegular />}>Nettoyage</Tab>
        <Tab value="analysis" icon={<DataAreaRegular />}>Analyse</Tab>
        {!isCompactTabs && <Tab value="chart" icon={<DataLineRegular />}>Graphique</Tab>}
        {!isCompactTabs && <Tab value="summary" icon={<DocumentRegular />}>Resume</Tab>}
        {!isCompactTabs && <Tab value="translate" icon={<TranslateRegular />}>Traduire</Tab>}
        {!isCompactTabs && <Tab value="functions" icon={<CodeRegular />}>Fonctions (M365)</Tab>}
        {!isCompactTabs && <Tab value="natural-language" icon={<MoreHorizontalRegular />}>Opérations</Tab>}
        {!isCompactTabs && <Tab value="dashboard" icon={<DataLineRegular />}>Dashboard</Tab>}
        {!isCompactTabs && <Tab value="report-builder" icon={<DocumentRegular />}>Rapport</Tab>}
        {!isCompactTabs && <Tab value="spreadsheet-builder" icon={<SparkleRegular />}>Feuille</Tab>}
        {!isCompactTabs && <Tab value="ai-agent" icon={<BotRegular />}>Agent</Tab>}
        {!isCompactTabs && <Tab value="ai-labs" icon={<BotRegular />}>IA Avancee</Tab>}
        {!isCompactTabs && <Tab value="workbook-brain" icon={<BrainRegular />}>Audit Classeur</Tab>}
        {!isCompactTabs && <Tab value="help" icon={<QuestionCircleRegular />}>Aide</Tab>}
        {!isCompactTabs && <Tab value="settings" icon={<SettingsRegular />}>Configurer AI</Tab>}
        {isCompactTabs && <Tab value="more" icon={<MoreHorizontalRegular />}>Plus</Tab>}
      </TabList>
      {isCompactTabs && (showMoreTabs || selectedTabValue === 'more') && (
        <div className="app-more-tabs">
          {secondaryTabs.map((tab) => (
            <Button
              key={tab.value}
              size="small"
              appearance={activePage === tab.value ? 'primary' : 'secondary'}
              icon={tab.icon()}
              onClick={() => handleContextNavigate(tab.value)}
            >
              {tab.label}
            </Button>
          ))}
        </div>
      )}
      <SmartContextBar
        context={excelContext}
        activePage={activePage}
        onNavigate={handleContextNavigate}
      />
      <div className="page-content">{renderPage()}</div>
    </div>
  );
};
